/**
 * 图片压缩工具 — 基于 Canvas，无需额外依赖
 * 用于 OCR 身份证识别等场景，压缩大图片以避免上传超时
 */

const DEFAULT_OPTIONS = {
  maxSizeMB: 2,          // 目标最大文件大小（MB）
  maxWidth: 1920,         // 最大宽度（px）
  maxHeight: 1920,        // 最大高度（px）
  quality: 0.8,           // 初始 JPEG 质量
  minQuality: 0.4,        // 最低质量下限
};

/**
 * 加载图片文件为 HTMLImageElement
 */
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

/**
 * 将 canvas 转为 Blob
 */
const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

/**
 * 压缩图片文件
 * @param {File} file - 原始图片文件
 * @param {object} [options] - 压缩选项
 * @returns {Promise<File>} 压缩后的 File 对象
 */
const compressImage = async (file, options = {}) => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const maxSizeBytes = opts.maxSizeMB * 1024 * 1024;

  // 文件已经足够小，无需压缩
  if (file.size <= maxSizeBytes) {
    return file;
  }

  const img = await loadImage(file);
  let { width, height } = img;

  // 按比例缩放尺寸
  if (width > opts.maxWidth || height > opts.maxHeight) {
    const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // 输出为 JPEG（压缩率更高），逐步降低 quality 直到满足大小要求
  const outputType = 'image/jpeg';
  let quality = opts.quality;
  let blob = await canvasToBlob(canvas, outputType, quality);

  while (blob.size > maxSizeBytes && quality > opts.minQuality) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, outputType, quality);
  }

  return new File([blob], file.name, { type: outputType, lastModified: Date.now() });
};

export default compressImage;
